import ReactGA from 'react-ga4';
import { DeferredCapture } from '../../../util/DeferredCapture.esm.js';

class GoogleAnalytics4 {
  customUserIdTransform;
  capture;
  contentGroupBy;
  allowedContexts;
  allowedAttributes;
  /**
   * Instantiate the implementation and initialize ReactGA.
   * @param options - initializes Google Analytics module with the config
   */
  constructor(options) {
    const {
      identity,
      measurementId,
      identityApi,
      userIdTransform = "sha-256",
      testMode,
      debug,
      cookieFlags,
      enableSendPageView,
      contentGroupBy,
      allowedContexts,
      allowedAttributes
    } = options;
    ReactGA.initialize(measurementId, {
      testMode,
      gaOptions: {
        cookieFlags,
        debug_mode: debug
      },
      gtagOptions: {
        cookie_flags: cookieFlags,
        debug_mode: debug,
        send_page_view: enableSendPageView
      }
    });
    this.contentGroupBy = contentGroupBy;
    this.allowedAttributes = allowedAttributes;
    this.allowedContexts = allowedContexts;
    this.capture = new DeferredCapture({ defer: identity === "required" });
    this.customUserIdTransform = typeof userIdTransform === "function" ? userIdTransform : void 0;
    if (identity !== "disabled") {
      if (identityApi) {
        this.setUserFrom(identityApi).then(() => {
          return;
        });
      }
    }
  }
  /**
   * Instantiate a fully configured GA Analytics API implementation.
   * @param config - Config object from app config
   * @param options - options with identityApi and userIdTransform config
   */
  static fromConfig(config, options = {}) {
    const measurementId = config.getString("app.analytics.ga4.measurementId");
    const identity = config.getOptionalString("app.analytics.ga4.identity") || "disabled";
    const debug = config.getOptionalBoolean("app.analytics.ga4.debug") ?? false;
    const cookieFlags = config.getOptionalString("app.analytics.ga4.cookieFlags") ?? "SameSite=Lax";
    const enableSendPageView = config.getOptionalBoolean("app.analytics.ga4.enableSendPageView") ?? false;
    const testMode = config.getOptionalBoolean("app.analytics.ga4.testMode") ?? false;
    const contentGroupBy = config.getOptionalString(
      "app.analytics.ga4.contentGrouping"
    );
    const allowedContexts = config.getOptionalStringArray(
      "app.analytics.ga4.allowedContexts"
    );
    const allowedAttributes = config.getOptionalStringArray(
      "app.analytics.ga4.allowedAttributes"
    );
    if (identity === "required" && !options.identityApi) {
      throw new Error(
        "Invalid config: identity API must be provided to deps when ga4.identity is required"
      );
    }
    return new GoogleAnalytics4({
      ...options,
      identity,
      measurementId,
      testMode,
      debug,
      cookieFlags,
      enableSendPageView,
      contentGroupBy,
      allowedContexts,
      allowedAttributes
    });
  }
  /**
   * Primary event capture implementation. Handles core navigate event as a
   * pageview and the rest as custom events. All custom dimensions/metrics are
   * applied as they should be (set on pageview, merged object on events).
   * @param event - AnalyticsEvent type captured
   */
  captureEvent(event) {
    const { context, action, subject, value, attributes } = event;
    const customEventData = this.setEventParameters(context, attributes);
    if (this.contentGroupBy) {
      customEventData.content_group = context[this.contentGroupBy];
    }
    const extensionId = context.extensionId || context.extension;
    const category = extensionId ? String(extensionId) : "App";
    if (action === "navigate" && category.toLocaleLowerCase("en-US").startsWith("app")) {
      this.capture.event(
        {
          category,
          action: "page_view",
          label: subject,
          value
        },
        customEventData
      );
      return;
    }
    if (action === "search") {
      customEventData.search_term = subject;
    }
    this.capture.event(
      {
        category,
        action,
        label: subject,
        value
      },
      customEventData
    );
  }
  /**
   * Returns an object of dimensions/metrics given an Analytics Context and an
   * Event Attributes, e.g. `{ c_pluginId: "some value", a_attribute1: 42 }`
   * @param context - analytics context object
   * @param attributes-  additional analytics event attributes
   */
  setEventParameters(context, attributes = {}) {
    const customEventParameters = {};
    const contextKeys = this.allowedContexts?.join("") === "*" ? Object.keys(context) : this.allowedContexts;
    contextKeys?.forEach((ctx) => {
      if (context[ctx] !== void 0 && context[ctx] !== null && context[ctx] !== "") {
        customEventParameters[`c_${ctx}`] = context[ctx];
      }
    });
    const attrKeys = this.allowedAttributes?.join("") === "*" ? Object.keys(attributes) : this.allowedAttributes;
    attrKeys?.forEach((attr) => {
      if (attributes[attr] !== void 0 && attributes[attr] !== null && attributes[attr] !== "") {
        customEventParameters[`a_${attr}`] = attributes[attr];
      }
    });
    return customEventParameters;
  }
  /**
   * Sets the GA userId, based on the `userEntityRef` set on the backstage
   * identity loaded from a given Backstage Identity API instance. Because
   * Google forbids sending any PII (including on the userId field), we hash
   * the entire `userEntityRef` on behalf of integrators:
   *
   * - With value `User:default/name`, userId becomes `sha256(User:default/name)`
   *
   * If an integrator wishes to use an alternative hashing mechanism or an
   * entirely different value, they may do so by passing a `userIdTransform`
   * function alongside the `identityApi` to `GoogleAnalytics.fromConfig()`.
   * This function receives the `userEntityRef` as an argument and should
   * resolve to a hashed version of whatever identifier they choose.
   *
   * Note: this feature requires that an integrator has set up a Google
   * Analytics User ID view in the property used to track Backstage.
   * @param identityApi - IdentityApi object
   */
  async setUserFrom(identityApi) {
    const { userEntityRef } = await identityApi.getBackstageIdentity();
    const userId = await this.getPrivateUserId(userEntityRef);
    ReactGA.set({ user_id: userId });
    this.capture.setReady();
  }
  /**
   * Returns a PII-free (according to Google's terms of service) user ID for
   * use in Google Analytics.
   * @param userEntityRef - user entity as string
   */
  getPrivateUserId(userEntityRef) {
    if (this.customUserIdTransform) {
      return this.customUserIdTransform(userEntityRef);
    }
    return this.hash(userEntityRef);
  }
  /**
   * Simple hash function; relies on web cryptography + the sha-256 algorithm.
   * @param value - value to be hashed
   */
  async hash(value) {
    const digest = await window.crypto.subtle.digest(
      "sha-256",
      new TextEncoder().encode(value)
    );
    const hashArray = Array.from(new Uint8Array(digest));
    return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
  }
}

export { GoogleAnalytics4 };
//# sourceMappingURL=GoogleAnalytics4.esm.js.map
